import type { FieldDef } from "@/lib/api/types";
import { masterCodeOf } from "@/lib/api/types";
import { widgetFor } from "@/lib/fieldTypes";
import type { FormValue } from "@/lib/form";

import styles from "./masters.module.css";

type Props = {
  field: FieldDef;
  value: FormValue;
  /** Rows of the master this field references (ref fields only). */
  options: { code: string; name: string }[];
  /** Current value of the field named by `dependsOn`, when there is one. */
  dependsOnValue?: string;
  onChange: (value: FormValue) => void;
};

/**
 * One configured field, rendered by catalog type.
 *
 * The widget is chosen from the shared catalog (`widgetFor`) unless the field
 * carries a `ui.widget` override, so a new input style becomes available to
 * every sport at once.
 */
export function FieldInput({
  field,
  value,
  options,
  dependsOnValue,
  onChange,
}: Props) {
  const id = `field-${field.key}`;
  const label = field.label ?? field.key;
  const required = field.required ?? false;
  const widget = field.ui?.widget ?? widgetFor(field.type);
  const isMasterRef = masterCodeOf(field.target) !== null;

  // A dependent field stays disabled until its parent is chosen — the operator
  // can't pick a player before naming the team the list is scoped to.
  const blockedByDependency = field.dependsOn !== undefined && !dependsOnValue;

  if (widget === "checkbox") {
    return (
      <div className={styles.checkboxField}>
        <input
          id={id}
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
        />
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required && <span className={styles.required}>*</span>}
      </label>

      {isMasterRef ? (
        <select
          id={id}
          className={styles.select}
          value={String(value)}
          required={required}
          disabled={blockedByDependency}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {options.map((option) => (
            <option key={option.code} value={option.code}>
              {option.name}
            </option>
          ))}
        </select>
      ) : widget === "select" ? (
        <select
          id={id}
          className={styles.select}
          value={String(value)}
          required={required}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label ?? String(option.value)}
            </option>
          ))}
        </select>
      ) : widget === "textarea" ? (
        <textarea
          id={id}
          className={styles.textarea}
          rows={4}
          value={String(value)}
          required={required}
          maxLength={field.maxLength}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          id={id}
          className={styles.input}
          type={inputType(field)}
          value={String(value)}
          required={required}
          min={field.min}
          max={field.max}
          step={field.step ?? (field.type === "integer" ? 1 : undefined)}
          maxLength={field.maxLength}
          pattern={field.pattern}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {blockedByDependency && (
        <span className={styles.help}>Choose {field.dependsOn} first.</span>
      )}
      {field.ui?.help && <span className={styles.help}>{field.ui.help}</span>}
      {field.unit && !field.ui?.help && <span className={styles.help}>In {field.unit}.</span>}
    </div>
  );
}

function inputType(field: FieldDef): string {
  if (field.type === "date") return "date";
  if (field.type === "datetime") return "datetime-local";
  return widgetFor(field.type) === "number" ? "number" : "text";
}
